import { useMutation, useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  INTERVIEW_LINK_HEADER,
  type BookInterviewInput,
  type CancelInterviewInput,
  type CandidateBookingView,
  type InterviewDetailsInput,
  type InterviewListView,
  type InterviewMineCountsView,
  type InterviewSummaryView,
  type InterviewView,
  type PanelOption,
  type RecordOutcomeInput,
  type RescheduleInterviewInput,
  type RoomView,
  type SchedulerView,
  type SubstitutePanelistInput,
  type TimeCheckView,
} from '@nest-recruit/shared';
import { apiGet, apiPost, apiPut, request, type ApiError } from '../../../lib/api-client';

/**
 * S19 — SCH-01…05. Realtime hints name `interviews`, so a panelist's "completed" shows
 * on the recruiter's open calendar and a candidate's booking appears without a reload.
 */

export const INTERVIEWS_KEY = ['interviews'] as const;
export const ROOMS_KEY = ['rooms'] as const;

export function interviewKey(id: string): readonly unknown[] {
  return [...INTERVIEWS_KEY, 'detail', id];
}

export function schedulerKey(id: string): readonly unknown[] {
  return [...INTERVIEWS_KEY, 'scheduler', id];
}

export interface InterviewListFilter {
  readonly view: InterviewListView;
  readonly from?: string;
  readonly to?: string;
  readonly isPast?: boolean;
  readonly jobId?: string;
}

export function useInterviews(filter: InterviewListFilter, isEnabled = true): UseQueryResult<readonly InterviewSummaryView[], ApiError> {
  return useQuery({
    queryKey: [...INTERVIEWS_KEY, 'list', filter],
    queryFn: () =>
      apiGet<readonly InterviewSummaryView[]>('/interviews', {
        query: {
          view: filter.view,
          ...(filter.from ? { from: filter.from } : {}),
          ...(filter.to ? { to: filter.to } : {}),
          ...(filter.isPast ? { isPast: 'true' } : {}),
          ...(filter.jobId ? { jobId: filter.jobId } : {}),
        },
      }),
    enabled: isEnabled,
    // §5a rule 2: the previous week stays on screen while the next one loads.
    placeholderData: (previous) => previous,
  });
}

/**
 * SCH-03's Coming up / Past counts. Under the lists' key, so whatever refreshes the lists (an
 * action settling, a realtime hint) refreshes the counts with them.
 */
export function useMineCounts(isEnabled = true): UseQueryResult<InterviewMineCountsView, ApiError> {
  return useQuery({
    queryKey: [...INTERVIEWS_KEY, 'list', 'mine-counts'],
    queryFn: () => apiGet<InterviewMineCountsView>('/interviews/mine-counts'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function useApplicationInterviews(applicationId: string, isEnabled: boolean): UseQueryResult<readonly InterviewSummaryView[], ApiError> {
  return useQuery({
    queryKey: [...INTERVIEWS_KEY, 'application', applicationId],
    queryFn: () => apiGet<readonly InterviewSummaryView[]>(`/applications/${applicationId}/interviews`),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function useInterview(id: string): UseQueryResult<InterviewView, ApiError> {
  return useQuery({ queryKey: interviewKey(id), queryFn: () => apiGet<InterviewView>(`/interviews/${id}`) });
}

export function useScheduler(id: string): UseQueryResult<SchedulerView, ApiError> {
  return useQuery({ queryKey: schedulerKey(id), queryFn: () => apiGet<SchedulerView>(`/interviews/${id}/scheduler`) });
}

/** The double-booking warning for a time typed by hand, asked before the recruiter commits. */
export function useTimeCheck(id: string, startsAt: string | null, roomId: string | null): UseQueryResult<TimeCheckView, ApiError> {
  return useQuery({
    queryKey: [...INTERVIEWS_KEY, 'check', id, startsAt, roomId],
    queryFn: () => apiGet<TimeCheckView>(`/interviews/${id}/check`, { query: { startsAt: startsAt ?? '', ...(roomId ? { roomId } : {}) } }),
    enabled: startsAt !== null,
    placeholderData: (previous) => previous,
  });
}

export function usePanelOptions(id: string, isEnabled: boolean): UseQueryResult<readonly PanelOption[], ApiError> {
  return useQuery({
    queryKey: [...INTERVIEWS_KEY, 'panel-options', id],
    queryFn: () => apiGet<readonly PanelOption[]>(`/interviews/${id}/panel-options`),
    enabled: isEnabled,
  });
}

export interface OfferResult {
  readonly interview: InterviewView;
  readonly linkUrl: string;
  readonly count: number;
}

type Versioned<T> = Omit<T, 'version'>;

export const interviewsApi = {
  offer: (id: string, version: number, startsAt: readonly string[]) => apiPost<OfferResult>(`/interviews/${id}/offer`, { version, startsAt }),
  book: (id: string, version: number, input: Versioned<BookInterviewInput>) => apiPost<InterviewView>(`/interviews/${id}/book`, { version, ...input }),
  reschedule: (id: string, version: number, input: Versioned<RescheduleInterviewInput>) => apiPost<InterviewView>(`/interviews/${id}/reschedule`, { version, ...input }),
  substitute: (id: string, version: number, input: Versioned<SubstitutePanelistInput>) => apiPost<InterviewView>(`/interviews/${id}/substitute`, { version, ...input }),
  cancel: (id: string, version: number, input: Versioned<CancelInterviewInput>) => apiPost<InterviewView>(`/interviews/${id}/cancel`, { version, ...input }),
  outcome: (id: string, version: number, input: Versioned<RecordOutcomeInput>) => apiPost<InterviewView>(`/interviews/${id}/outcome`, { version, ...input }),
  cantMakeIt: (id: string, version: number, note: string | null) => apiPost<InterviewView>(`/interviews/${id}/cant-make-it`, { version, note }),
  details: (id: string, version: number, input: Versioned<InterviewDetailsInput>) => apiPut<InterviewView>(`/interviews/${id}/details`, { version, ...input }),
  copyMessage: (id: string) => apiPost<{ readonly text: string }>(`/interviews/${id}/copy-message`),
};

// --- Rooms (F10.14, S19 Q5) ---------------------------------------------------------------

const ROOMS_LIST_KEY = [...ROOMS_KEY, 'list'] as const;
/** Every room write shares this, so the list refetches once the last of a quick run has settled. */
const ROOM_MUTATION_KEY = [...ROOMS_KEY, 'write'] as const;

export function useRooms(isEnabled = true): UseQueryResult<readonly RoomView[], ApiError> {
  return useQuery({
    queryKey: ROOMS_LIST_KEY,
    queryFn: () => apiGet<readonly RoomView[]>('/rooms'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

/** The server's order: venture, then name. */
function byVentureAndName(a: RoomView, b: RoomView): number {
  return a.ventureName.localeCompare(b.ventureName) || a.name.localeCompare(b.name);
}

function changeRooms(queryClient: QueryClient, change: (rows: readonly RoomView[]) => readonly RoomView[]): void {
  queryClient.setQueryData<readonly RoomView[]>(ROOMS_LIST_KEY, (rows) => (rows ? [...change(rows)].sort(byVentureAndName) : rows));
}

/** Puts a room in, replacing any copy of it (by id) — never another room. */
function withRoom(room: RoomView, replacing: string = room.id): (rows: readonly RoomView[]) => readonly RoomView[] {
  return (rows) => [...rows.filter((row) => row.id !== replacing && row.id !== room.id), room];
}

function withoutRoom(id: string): (rows: readonly RoomView[]) => readonly RoomView[] {
  return (rows) => rows.filter((row) => row.id !== id);
}

/**
 * A refetch already on its way would land without this change; and refetching after each write of a
 * quick run would make the next one blink out and back (CLAUDE.md §5a rule 3). While a write settles it
 * still counts as one in flight, hence `<= 1`.
 */
async function holdRooms(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries({ queryKey: ROOMS_KEY });
}

function refreshRoomsWhenQuiet(queryClient: QueryClient): void {
  if (queryClient.isMutating({ mutationKey: ROOM_MUTATION_KEY }) <= 1) void queryClient.invalidateQueries({ queryKey: ROOMS_KEY });
}

/** A room on screen before the server has answered. It can't be renamed or taken off until it has an id. */
const PENDING_ROOM_PREFIX = 'pending:';

export function isPendingRoom(room: RoomView): boolean {
  return room.id.startsWith(PENDING_ROOM_PREFIX);
}

export interface CreateRoomVariables {
  readonly ventureId: string;
  readonly ventureName: string;
  readonly name: string;
  /** Names the pending card, so the server's room replaces exactly it. */
  readonly pendingKey: string;
}

/**
 * Admin redesign 2026-11-02 (Rooms A): every room change shows at once and reconciles after
 * (non-negotiable #11). A refusal takes back only its own change, never a whole earlier copy of the
 * list, so a second change made meanwhile survives.
 */
export function useCreateRoom() {
  const queryClient = useQueryClient();
  return useMutation<RoomView, ApiError, CreateRoomVariables>({
    mutationKey: ROOM_MUTATION_KEY,
    mutationFn: ({ ventureId, name }) => apiPost<RoomView>('/rooms', { ventureId, name }),
    onMutate: async ({ ventureId, ventureName, name, pendingKey }) => {
      await holdRooms(queryClient);
      changeRooms(queryClient, withRoom({ id: `${PENDING_ROOM_PREFIX}${pendingKey}`, ventureId, ventureName, name, upcomingInterviews: 0, version: 0 }));
    },
    onError: (_error, { pendingKey }) => changeRooms(queryClient, withoutRoom(`${PENDING_ROOM_PREFIX}${pendingKey}`)),
    onSuccess: (room, { pendingKey }) => changeRooms(queryClient, withRoom(room, `${PENDING_ROOM_PREFIX}${pendingKey}`)),
    onSettled: () => refreshRoomsWhenQuiet(queryClient),
  });
}

export interface RenameRoomVariables {
  readonly room: RoomView;
  readonly name: string;
}

export function useRenameRoom() {
  const queryClient = useQueryClient();
  return useMutation<RoomView, ApiError, RenameRoomVariables>({
    mutationKey: ROOM_MUTATION_KEY,
    mutationFn: ({ room, name }) => apiPut<RoomView>(`/rooms/${room.id}`, { version: room.version, name }),
    onMutate: async ({ room, name }) => {
      await holdRooms(queryClient);
      changeRooms(queryClient, withRoom({ ...room, name }));
    },
    onError: (_error, { room }) => changeRooms(queryClient, withRoom(room)),
    onSuccess: (saved) => changeRooms(queryClient, withRoom(saved)),
    onSettled: () => refreshRoomsWhenQuiet(queryClient),
  });
}

/** Take off: gone from the list at once; a refusal (interviews booked since) puts it back. */
export function useArchiveRoom() {
  const queryClient = useQueryClient();
  return useMutation<{ readonly archived: boolean }, ApiError, RoomView>({
    mutationKey: ROOM_MUTATION_KEY,
    mutationFn: (room) => apiPost<{ readonly archived: boolean }>(`/rooms/${room.id}/archive`, { version: room.version }),
    onMutate: async (room) => {
      await holdRooms(queryClient);
      changeRooms(queryClient, withoutRoom(room.id));
    },
    onError: (_error, room) => changeRooms(queryClient, withRoom(room)),
    onSettled: () => refreshRoomsWhenQuiet(queryClient),
  });
}

/** The Undo on take off (`POST /rooms/:id/restore`): back at once; a refusal (its name taken since) takes it off again. */
export function useRestoreRoom() {
  const queryClient = useQueryClient();
  return useMutation<RoomView, ApiError, RoomView>({
    mutationKey: ROOM_MUTATION_KEY,
    mutationFn: (room) => apiPost<RoomView>(`/rooms/${room.id}/restore`),
    onMutate: async (room) => {
      await holdRooms(queryClient);
      changeRooms(queryClient, withRoom(room));
    },
    onError: (_error, room) => changeRooms(queryClient, withoutRoom(room.id)),
    onSuccess: (room) => changeRooms(queryClient, withRoom(room)),
    onSettled: () => refreshRoomsWhenQuiet(queryClient),
  });
}

// --- The candidate's page (D16: no account; the link travels in a header) -------------------

export function bookingKey(token: string): readonly unknown[] {
  return ['interview-booking', token];
}

function linkHeaders(token: string): Readonly<Record<string, string>> {
  return { [INTERVIEW_LINK_HEADER]: token };
}

export function useCandidateBooking(token: string): UseQueryResult<CandidateBookingView, ApiError> {
  return useQuery({
    queryKey: bookingKey(token),
    queryFn: async () => (await request<CandidateBookingView>('/interview-booking', { headers: linkHeaders(token) })).data,
    enabled: token !== '',
    retry: (count, error) => error.status === 0 && count < 3,
    refetchOnWindowFocus: true,
  });
}

export const bookingApi = {
  book: async (token: string, startsAt: string) => (await request<CandidateBookingView>('/interview-booking/book', { method: 'POST', body: { startsAt }, headers: linkHeaders(token) })).data,
  confirm: async (token: string) => (await request<CandidateBookingView>('/interview-booking/confirm', { method: 'POST', headers: linkHeaders(token) })).data,
};
